import * as React from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import { cn } from "@/frontend/lib/utils";

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  refs: ReturnType<typeof useFloating>["refs"];
  floatingStyles: React.CSSProperties;
  getReferenceProps: ReturnType<typeof useInteractions>["getReferenceProps"];
  getFloatingProps: ReturnType<typeof useInteractions>["getFloatingProps"];
  context: ReturnType<typeof useFloating>["context"];
}

const PopoverContext = React.createContext<PopoverContextValue | undefined>(undefined);

function usePopover() {
  const context = React.useContext(PopoverContext);
  if (!context) {
    throw new Error("Popover components must be used within a Popover");
  }
  return context;
}

interface PopoverProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Popover component using Floating UI for positioning
 * Provides floating content without Radix UI dependencies
 */
function Popover({ children, open: controlledOpen, onOpenChange }: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  return (
    <PopoverContext.Provider
      value={{
        open,
        setOpen,
        refs,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
        context,
      }}
    >
      {children}
    </PopoverContext.Provider>
  );
}

/**
 * PopoverTrigger - wraps children with click handler
 * No longer uses asChild pattern
 */
function PopoverTrigger({ children }: { children: React.ReactElement }) {
  const { refs, getReferenceProps } = usePopover();

  return React.cloneElement(
    children,
    getReferenceProps({
      ref: refs.setReference,
      ...(children.props as Record<string, unknown>),
    })
  );
}

interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: "start" | "center" | "end";
  sideOffset?: number;
}

/**
 * PopoverContent - the floating content
 */
const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ className, align: _align = "center", sideOffset: _sideOffset = 4, ...props }, _propRef) => {
    const { open, refs, floatingStyles, getFloatingProps, context } = usePopover();

    if (!open) return null;

    // Check if we're inside a dialog and use its portal container
    const dialogPortal = typeof document !== 'undefined' ? document.getElementById('dialog-portal') : null;

    return (
      <FloatingPortal root={dialogPortal}>
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps({
              className: cn(
                "z-[9999] w-72 rounded-md border border-gray-200 bg-white p-4 text-gray-950 shadow-md outline-none",
                "animate-in fade-in-0 zoom-in-95 duration-200",
                className
              ),
              ...props,
            })}
          />
        </FloatingFocusManager>
      </FloatingPortal>
    );
  }
);
PopoverContent.displayName = "PopoverContent";

export { Popover, PopoverTrigger, PopoverContent };